import React, { useState } from 'react';
import './RollsRequest.scss';
import Swal from 'sweetalert2';
import { TerminalsService } from '../../services/TerminalsService';
import { ReactComponent as NpsSVG } from "../../../node_modules/itd-common/assets/icons/nps.svg";
import { ReactComponent as CommerceSVG } from "../../../node_modules/itd-common/assets/icons/commerceNsp.svg";
import { ReactComponent as LogoSVG } from "../../../node_modules/itd-common/assets/icons/logoFiserv.svg";
import { ReactComponent as SuccessSVG } from "../../../node_modules/itd-common/assets/icons/rollrequest.svg";

type Props = {
    onClose: () => void;
    eventId: string;
    eventType?: string;
};

const ScoreRow: React.FC<{ value: number | null; onChange: (v: number) => void; min?: number; max?: number }> = ({ value, onChange, min = 0, max = 10 }) => (
    <div className={`nps-scores nps-scores--grid${max - min + 1 > 6 ? ' nps-scores--dense' : ''}`}>
        {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((i) => (
            <button
                key={i}
                className={`nps-score-btn${value === i ? ' selected' : ''}`}
                onClick={() => onChange(i)}
                type="button"
            >
                {i}
            </button>
        ))}
    </div>
);

const NpsSurvey: React.FC<Props> = ({ onClose, eventId, eventType = 'System' }) => {
    const [step, setStep] = useState<number>(0);
    const [score1, setScore1] = useState<number | null>(null);
    const [score2, setScore2] = useState<number | null>(null);
    const [commentMc, setCommentMc] = useState<string>('');
    const [commentFiserv, setCommentFiserv] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    const handleClose = () => {
        if (step < 5) {
            localStorage.setItem('nps_last_shown', Date.now().toString());
        }
        onClose();
    };

    const handleSubmit = () => {
        if (submitting) return;
        setSubmitting(true);
        TerminalsService.sendNps({
            merchantCenterScore: score1!,
            fiservBrandScore: score2!,
            scoreReasonMc: commentMc,
            scoreReasonFiserv: commentFiserv,
            eventId,
            eventType
        }).then((res) => {
            if (res.status === 200) {
                localStorage.setItem('nps_last_submitted', Date.now().toString());
                setStep(5);
            } else {
                setSubmitting(false);
                Swal.fire({
                    icon: 'error',
                    title: 'No pudimos registrar tu respuesta',
                    text: 'Ocurrió un problema al enviar la encuesta. Por favor, intentá nuevamente más tarde.',
                    confirmButtonText: 'Cerrar',
                    confirmButtonColor: '#f60'
                });
            }
        }).catch(() => {
            setSubmitting(false);
            Swal.fire({
                icon: 'error',
                title: 'No pudimos registrar tu respuesta',
                text: 'Ocurrió un problema al enviar la encuesta. Por favor, intentá nuevamente más tarde.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#f60'
            });
        });
    };

    const renderStep = () => {
        switch (step) {
            case 0:
                return (
                    <>
                        <button className="nps-skip-btn" onClick={handleClose} type="button" aria-label="Cerrar">✕</button>
                        <div className="nps-img-wrapper">
                            <NpsSVG />
                        </div>
                        <h3 className="nps-title">Tu opinión nos ayuda a mejorar</h3>
                        <p className="nps-subtitle">
                            Queremos conocer tu experiencia para seguir mejorando nuestro servicio. Te tomará menos de 1 minuto.
                        </p>
                        <div className="nps-actions">
                            <button className="nps-btn-secondary" onClick={handleClose} type="button">Prefiero no responder</button>
                            <button className="nps-btn-primary" onClick={() => setStep(1)} type="button">Comenzar</button>
                        </div>
                    </>
                );
            case 1:
                return (
                    <>
                        <button className="nps-skip-btn" onClick={handleClose} type="button" aria-label="Cerrar">✕</button>
                        <div className="nps-img-wrapper">
                            <CommerceSVG />
                        </div>
                        <p className="nps-title">
                            ¿Cuán satisfecho estás con el uso de esta plataforma de gestión “Merchant Center”?
                        </p>
                        <ScoreRow value={score1} onChange={(v) => { setScore1(v); setTimeout(() => setStep(2), 300); }} min={1} max={5} />
                        <p className="nps-subtitle">Donde 1 es nada probable y 5 es extremadamente probable</p>
                    </>
                );
            case 2:
                return (
                    <>
                        <button className="nps-skip-btn" onClick={handleClose} type="button" aria-label="Cerrar">✕</button>
                        <div className="nps-img-wrapper">
                            <CommerceSVG />
                        </div>
                        <p className="nps-title">¿Cuál es el principal motivo de tu calificación?</p>
                        <p className="nps-subtitle">Tu calificación fue: <strong>{score1}</strong></p>
                        <textarea
                            className="nps-comment"
                            placeholder="Escribe el motivo"
                            value={commentMc}
                            onChange={e => setCommentMc(e.target.value)}
                            rows={4}
                            maxLength={500}
                        />
                        <p className="nps-comment-hint">
                            {`${commentMc.length}/500 caracteres`}
                        </p>
                        <div className="nps-actions">
                            <button className="nps-btn-secondary" onClick={handleClose} type="button">Prefiero no responder</button>
                            <button className="nps-btn-primary" onClick={() => setStep(3)} type="button">
                                Siguiente
                            </button>
                        </div>
                    </>
                );
            case 3:
                return (
                    <>
                        <button className="nps-skip-btn" onClick={handleClose} type="button" aria-label="Cerrar">✕</button>
                        <div className="nps-img-wrapper">
                            <LogoSVG />
                        </div>
                        <p className="nps-title">
                            ¿Qué tan probable es que recomiende a Fiserv a un comercio conocido?
                        </p>
                        <ScoreRow value={score2} onChange={(v) => { setScore2(v); setTimeout(() => setStep(4), 300); }} />
                        <p className="nps-subtitle">Donde 0 es nada probable y 10 es extremadamente probable</p>
                    </>
                );
            case 4:
                return (
                    <>
                        <button className="nps-skip-btn" onClick={handleClose} type="button" aria-label="Cerrar">✕</button>
                        <div className="nps-img-wrapper">
                            <LogoSVG />
                        </div>
                        <p className="nps-title">¿Cuál es el principal motivo de tu calificación?</p>
                        <p className="nps-subtitle">Tu calificación fue: <strong>{score2}</strong></p>
                        <textarea
                            className="nps-comment"
                            placeholder="Escribe el motivo"
                            value={commentFiserv}
                            onChange={e => setCommentFiserv(e.target.value)}
                            rows={4}
                            maxLength={500}
                        />
                        <p className="nps-comment-hint">
                            {`${commentFiserv.length}/500 caracteres`}
                        </p>
                        <div className="nps-actions">
                            <button className="nps-btn-secondary" onClick={handleClose} type="button">Prefiero no responder</button>
                            <button className="nps-btn-primary" onClick={handleSubmit} type="button" disabled={submitting}>
                                {submitting ? 'Enviando...' : 'Enviar'}
                            </button>
                        </div>
                    </>
                );
            case 5:
            default:
                return (
                    <>
                        <button className="nps-skip-btn" onClick={handleClose} type="button" aria-label="Cerrar">✕</button>
                        <div className="nps-img-wrapper">
                            <SuccessSVG />
                        </div>
                        <h3 className="nps-title">¡Gracias por compartir tu opinión!</h3>
                        <p className="nps-subtitle">
                            Cada respuesta nos acerca a construir una mejor experiencia para vos y para miles de comercios que usan Merchant Center todos los días.
                        </p>
                    </>
                );
        }
    };

    return (
        <>
            <div className="nps-overlay">
                <div className="nps-modal-card">
                    {renderStep()}
                </div>
            </div>
        </>
    );
};

export default NpsSurvey;
